import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { toast } from "sonner";
import {
  Calculator, Save, FileText, Calendar as CalendarIcon, RefreshCw,
  TrendingUp, ArrowDownRight, ArrowUpRight, CheckCircle2, AlertTriangle, History,
  ArrowDownCircle, ArrowUpCircle, HandCoins, X,
} from "lucide-react";

// Standard-EUR-Stückelungen (Größe → Kleinste)
const DENOM = [
  { v: 500, l: "500 €", kind: "Schein" },
  { v: 200, l: "200 €", kind: "Schein" },
  { v: 100, l: "100 €", kind: "Schein" },
  { v:  50, l: "50 €",  kind: "Schein" },
  { v:  20, l: "20 €",  kind: "Schein" },
  { v:  10, l: "10 €",  kind: "Schein" },
  { v:   5, l: "5 €",   kind: "Schein" },
  { v:   2, l: "2 €",   kind: "Münze" },
  { v:   1, l: "1 €",   kind: "Münze" },
  { v: 0.50, l: "50 ¢", kind: "Münze" },
  { v: 0.20, l: "20 ¢", kind: "Münze" },
  { v: 0.10, l: "10 ¢", kind: "Münze" },
  { v: 0.05, l: "5 ¢",  kind: "Münze" },
  { v: 0.02, l: "2 ¢",  kind: "Münze" },
  { v: 0.01, l: "1 ¢",  kind: "Münze" },
];

const today = () => new Date().toISOString().slice(0, 10);
const fmtEUR = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const fmtDate = (iso) => {
  if (!iso) return "";
  const [d, t] = String(iso).split("T");
  const [y, mo, da] = (d || "").split("-");
  return `${da}.${mo}.${y}${t ? " · " + t.slice(0, 5) : ""}`;
};

export default function Kassensturz() {
  const [date, setDate] = useState(today());
  const [cashStart, setCashStart] = useState(0);
  const [counts, setCounts] = useState(() => Object.fromEntries(DENOM.map((d) => [d.v.toString(), 0])));
  const [preview, setPreview] = useState(null); // { by_payment, gross, vat, order_count, cancel_sum, cancel_count, tse, cash_movements }
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [forceCorrection, setForceCorrection] = useState(false);
  const [movementModal, setMovementModal] = useState(null); // { kind }

  const loadPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/pos/z-report/preview?date=${date}`);
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Laden"); }
    finally { setBusy(false); }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/pos/z-reports");
      setHistory(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadPreview(); loadHistory(); }, []);
  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [date]);

  const cashIst = useMemo(() => {
    return DENOM.reduce((s, d) => s + d.v * (Number(counts[d.v.toString()]) || 0), 0);
  }, [counts]);

  const movementSum = Number(preview?.cash_movement_sum || 0);

  const cashSoll = useMemo(() => {
    const bar = Number(preview?.by_payment?.bar || 0);
    const start = Number(cashStart) || 0;
    return start + bar + movementSum;
  }, [preview, cashStart, movementSum]);

  const diff = cashIst - cashSoll;
  const diffOk = Math.abs(diff) < 0.01;

  const update = (v, val) => {
    const parsed = parseInt(val || 0, 10);
    setCounts((c) => ({ ...c, [v]: Number.isFinite(parsed) && parsed > 0 ? parsed : 0 }));
  };
  const resetCounts = () => setCounts(Object.fromEntries(DENOM.map((d) => [d.v.toString(), 0])));

  const save = async () => {
    if (!preview) return toast.error("Erst Tageszusammenfassung laden");
    setBusy(true);
    try {
      const denominations = DENOM.map((d) => ({ value: d.v, count: Number(counts[d.v.toString()]) || 0 }));
      const { data } = await api.post("/admin/pos/z-report", {
        date, cash_start: Number(cashStart) || 0, denominations, force: forceCorrection,
      });
      toast.success(`Z-Bericht #${data.report_number} erstellt${data.is_correction ? " (Korrektur)" : ""}`);
      setForceCorrection(false);
      loadHistory();
      // Auto-open PDF
      openPdf(`/admin/pos/z-report/${data.id}/pdf`);
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen";
      if (status === 409) {
        toast.error(detail);
        setForceCorrection(true);  // Nächster Klick erzeugt Korrektur-Bericht
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  const submitMovement = async ({ kind, amount, reason }) => {
    if (!amount || amount <= 0) return toast.error("Betrag muss > 0 sein");
    try {
      await api.post("/pos/cash-movement", { kind, amount: Number(amount), reason });
      toast.success(kind === "einlage" ? "Einlage gebucht" : "Entnahme gebucht");
      setMovementModal(null);
      loadPreview();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    }
  };

  return (
    <div className="space-y-6" data-testid="kassensturz-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Calculator size={22} className="text-[#D4AF37]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Tagesabschluss</div>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Z-Bericht · Kassensturz</h1>
            <div className="text-[10px] text-[#575148] mt-1">
              Soll/Ist-Vergleich · DSFinV-K-konformer PDF-Export · Denominations-Zählung
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1">
              <CalendarIcon size={11} /> Kassentag
            </label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="kassensturz-date"
              className="mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>
          <button onClick={loadPreview} data-testid="kassensturz-reload"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded text-[11px] uppercase tracking-widest hover:bg-[#D4AF37]/10">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Aktualisieren
          </button>
        </div>
      </div>

      {/* Live-Zusammenfassung des Tages */}
      <div className="grid md:grid-cols-4 gap-3">
        <Stat label="Belege" value={preview?.order_count ?? "—"} sub="ohne Stornos" icon={<TrendingUp size={13} />} />
        <Stat label="Brutto-Umsatz" value={fmtEUR(preview?.gross)} sub={`Netto ${fmtEUR(preview?.vat?.net)}`} icon={<ArrowUpRight size={13} />} gold />
        <Stat label="Bar-Umsatz" value={fmtEUR(preview?.by_payment?.bar)} sub="Grundlage SOLL-Kasse" icon={<ArrowDownRight size={13} />} />
        <Stat label="Storno" value={fmtEUR(preview?.cancel_sum)} sub={`${preview?.cancel_count || 0} Beleg(e)`} icon={<AlertTriangle size={13} />} accent={(preview?.cancel_count || 0) > 0} />
      </div>

      {/* Zahlarten-Übersicht */}
      {preview?.by_payment && Object.keys(preview.by_payment).length > 0 && (
        <div className="card-lux p-4">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">Umsatz je Zahlart · {date}</div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(preview.by_payment).map(([pay, sum]) => (
              <div key={pay} className="px-3 py-2 border border-[#2A2A2A] rounded" data-testid={`kassensturz-pay-${pay}`}>
                <div className="text-[9px] uppercase tracking-widest text-[#575148]">{pay}</div>
                <div className="font-heading text-[#D4AF37] mt-0.5">{fmtEUR(sum)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kassenbewegungen + Trinkgeld */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="card-lux p-4" data-testid="cash-movements-card">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <HandCoins size={16} className="text-[#D4AF37]" />
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Kassenbewegungen · Bar</div>
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => setMovementModal({ kind: "einlage" })} data-testid="cash-movement-einlage"
                className="text-[10px] uppercase tracking-widest border border-[#8FBC8F]/50 text-[#8FBC8F] px-2 py-1 rounded hover:bg-[#8FBC8F]/10 inline-flex items-center gap-1">
                <ArrowDownCircle size={11} /> Einlage
              </button>
              <button onClick={() => setMovementModal({ kind: "entnahme" })} data-testid="cash-movement-entnahme"
                className="text-[10px] uppercase tracking-widest border border-[#EE9CA1]/50 text-[#EE9CA1] px-2 py-1 rounded hover:bg-[#EE9CA1]/10 inline-flex items-center gap-1">
                <ArrowUpCircle size={11} /> Entnahme
              </button>
            </div>
          </div>
          <div className="text-xs text-[#A8A398] mb-2 font-mono">
            Bewegungssaldo heute: <span className={movementSum >= 0 ? "text-[#8FBC8F]" : "text-[#EE9CA1]"}>{movementSum >= 0 ? "+" : ""}{fmtEUR(movementSum)}</span>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="cash-movement-list">
            {(preview?.cash_movements || []).length === 0 && (
              <div className="text-[10px] text-[#575148] italic py-2">Keine Bewegungen erfasst</div>
            )}
            {(preview?.cash_movements || []).map((m) => (
              <div key={m.id} className="flex items-center justify-between text-xs border-b border-[#141414] py-1.5">
                <div>
                  <span className={`uppercase tracking-widest text-[9px] mr-2 ${m.signed_amount >= 0 ? "text-[#8FBC8F]" : "text-[#EE9CA1]"}`}>{m.kind}</span>
                  <span className="text-[#A8A398]">{m.reason || "—"}</span>
                </div>
                <span className={`font-mono ${m.signed_amount >= 0 ? "text-[#8FBC8F]" : "text-[#EE9CA1]"}`}>
                  {m.signed_amount >= 0 ? "+" : ""}{fmtEUR(m.signed_amount)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-lux p-4">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 flex items-center gap-2">
            <HandCoins size={14} /> Trinkgeld heute
          </div>
          <div className="font-heading text-3xl text-[#EEDC82]" data-testid="tip-total">{fmtEUR(preview?.tip_total)}</div>
          <div className="text-[10px] text-[#575148] mt-1 uppercase tracking-widest">
            Getrennt gebucht · nicht steuerbar
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-6">
        {/* Denominations-Tabelle */}
        <div className="card-lux p-5" data-testid="kassensturz-denominations">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <h3 className="font-heading text-2xl">Kassenzählung · Stückelungen</h3>
            <button onClick={resetCounts} className="text-[10px] uppercase tracking-widest text-[#A8A398] hover:text-[#EE9CA1]">
              Zurücksetzen
            </button>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
            {DENOM.map((d) => {
              const cnt = Number(counts[d.v.toString()]) || 0;
              const sub = d.v * cnt;
              return (
                <div key={d.v} className="flex items-center gap-2 border-b border-[#141414] py-1.5">
                  <span className={`w-16 text-sm ${d.kind === "Schein" ? "text-[#EEDC82]" : "text-[#A8A398]"}`}>{d.l}</span>
                  <span className="text-[9px] uppercase tracking-widest text-[#575148] w-10">{d.kind}</span>
                  <input type="number" min="0" value={cnt} onChange={(e) => update(d.v.toString(), e.target.value)}
                    data-testid={`kassensturz-count-${d.v.toString().replace(".", "-")}`}
                    className="w-20 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-right font-mono text-sm focus:border-[#D4AF37] outline-none" />
                  <span className="ml-auto font-mono text-xs text-[#A8A398] w-24 text-right">
                    {sub > 0 ? fmtEUR(sub) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Soll/Ist */}
        <aside className="card-lux p-5 h-fit" data-testid="kassensturz-summary">
          <h3 className="font-heading text-2xl mb-4">Kassensturz</h3>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Anfangs­bestand (€)</label>
              <input type="number" step="0.01" value={cashStart} onChange={(e) => setCashStart(e.target.value)}
                data-testid="kassensturz-cash-start"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono focus:border-[#D4AF37] outline-none" />
            </div>
            <div className="pt-3 border-t border-[#1A1A1A] space-y-1.5">
              <Row label="Anfangsbestand" value={fmtEUR(cashStart)} />
              <Row label="Bar-Umsatz (heute)" value={fmtEUR(preview?.by_payment?.bar)} />
              {movementSum !== 0 && (
                <Row label={movementSum >= 0 ? "+ Einlagen" : "− Entnahmen"} value={`${movementSum >= 0 ? "+" : ""}${fmtEUR(movementSum)}`} />
              )}
              <Row label="SOLL" value={fmtEUR(cashSoll)} bold />
            </div>
            <div className="pt-3 border-t border-[#1A1A1A] space-y-1.5">
              <Row label="IST (gezählt)" value={fmtEUR(cashIst)} bold />
            </div>
            <div className={`pt-3 border-t rounded-lg p-3 ${diffOk ? "border-[#8FBC8F]/40 bg-[#0F1F0F]/30" : "border-[#7A1921]/50 bg-[#1F0F0F]/30"}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-[#A8A398]">Differenz</span>
                {diffOk
                  ? <CheckCircle2 size={16} className="text-[#8FBC8F]" />
                  : <AlertTriangle size={16} className="text-[#EE9CA1]" />}
              </div>
              <div className={`font-heading text-2xl mt-1 ${diffOk ? "text-[#8FBC8F]" : "text-[#EE9CA1]"}`} data-testid="kassensturz-diff">
                {diff > 0 ? "+" : ""}{fmtEUR(diff)}
              </div>
              <div className="text-[9px] text-[#575148] mt-1 uppercase tracking-widest">
                {diffOk ? "Kasse stimmt · buchbar" : diff < 0 ? "Fehlbetrag" : "Kassenplus"}
              </div>
            </div>
            <button onClick={save} disabled={busy || !preview} data-testid="kassensturz-save"
              className={`w-full mt-2 py-3 rounded uppercase tracking-widest text-xs flex items-center justify-center gap-2 disabled:opacity-40 ${forceCorrection ? "bg-[#7A1921] hover:bg-[#951F27] text-white" : "bg-[#D4AF37] hover:bg-[#EEDC82] text-black"}`}>
              <Save size={14} /> {forceCorrection ? "Korrektur-Bericht speichern" : "Z-Bericht speichern & PDF öffnen"}
            </button>
            {forceCorrection && (
              <div className="text-[10px] text-[#EE9CA1] mt-1 uppercase tracking-widest">
                ⚠ Tagesabschluss existiert bereits — beim Speichern wird das Original archiviert und ein Korrektur-Bericht (Suffix -K) erstellt.
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Historie */}
      <div className="card-lux p-4">
        <div className="flex items-center gap-2 mb-3">
          <History size={16} className="text-[#D4AF37]" />
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Bereits erstellte Z-Berichte</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="kassensturz-history">
            <thead>
              <tr className="border-b border-[#1A1A1A] text-[10px] uppercase tracking-widest text-[#575148]">
                <th className="text-left px-3 py-2">Bericht-Nr</th>
                <th className="text-left px-3 py-2">Kassentag</th>
                <th className="text-left px-3 py-2">Erstellt</th>
                <th className="text-right px-3 py-2">Brutto</th>
                <th className="text-right px-3 py-2">SOLL</th>
                <th className="text-right px-3 py-2">IST</th>
                <th className="text-right px-3 py-2">Diff</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-[#575148]">Noch kein Z-Bericht erstellt</td></tr>
              )}
              {history.map((r) => (
                <tr key={r.id} className="border-b border-[#141414] hover:bg-[#D4AF37]/[0.03]" data-testid={`z-report-row-${r.report_number}`}>
                  <td className="px-3 py-2 font-mono text-[#EEDC82]">{r.report_number}</td>
                  <td className="px-3 py-2 text-[#F5F0E6]">{r.date}</td>
                  <td className="px-3 py-2 text-xs text-[#A8A398] font-mono">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtEUR(r.gross)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#A8A398]">{fmtEUR(r.cash_soll)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#F5F0E6]">{fmtEUR(r.cash_ist)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${Math.abs(r.cash_diff) < 0.01 ? "text-[#8FBC8F]" : "text-[#EE9CA1]"}`}>
                    {r.cash_diff > 0 ? "+" : ""}{fmtEUR(r.cash_diff)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openPdf(`/admin/pos/z-report/${r.id}/pdf`)}
                      data-testid={`z-report-pdf-${r.report_number}`}
                      className="text-xs px-2 py-1 border border-[#D4AF37]/40 rounded hover:bg-[#D4AF37] hover:text-black text-[#D4AF37] inline-flex items-center gap-1">
                      <FileText size={11} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] text-[#575148] italic border-t border-[#1A1A1A] pt-3">
        <b>Hinweis DSFinV-K/§146a AO:</b> Der Z-Bericht ist die abschließende Tages-Zusammenfassung. Die
        TSE-Signatur (HMAC-SHA256) ist auf allen Einzelbelegen enthalten. Für eine BSI-zertifizierte
        Hardware-TSE wird ein separates Gerät benötigt.
      </div>

      {movementModal && <MovementModal kind={movementModal.kind} onClose={() => setMovementModal(null)} onSubmit={submitMovement} />}
    </div>
  );
}

function MovementModal({ kind, onClose, onSubmit }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const isEinlage = kind === "einlage";
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="movement-modal">
      <div className="relative w-full max-w-md bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg p-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[#F5F0E6]/70 hover:text-[#D4AF37]"><X size={22} /></button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">
          {isEinlage ? "Bar-Einlage" : "Bar-Entnahme"}
        </div>
        <h2 className="font-heading text-2xl mt-1">{isEinlage ? "Geld einlegen" : "Geld entnehmen"}</h2>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Betrag (€)</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="movement-amount" autoFocus
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono focus:border-[#D4AF37] outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Grund / Beleg-Nr</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={isEinlage ? "z. B. Startbestand, Wechselgeld" : "z. B. Bareinkauf Getränke Beleg #4711"}
              data-testid="movement-reason"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={onClose}
            className="flex-1 border border-[#2A2A2A] text-[#A8A398] px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:border-[#D4AF37]">
            Abbrechen
          </button>
          <button onClick={() => onSubmit({ kind, amount, reason })} data-testid="movement-submit"
            className={`flex-1 px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] ${isEinlage ? "bg-[#8FBC8F] hover:bg-[#A7D4A7] text-black" : "bg-[#7A1921] hover:bg-[#951F27] text-white"}`}>
            Buchen
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, icon, gold, accent }) {
  return (
    <div className={`card-lux p-4 ${accent ? "border-[#EE9CA1]/30" : ""}`}>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.32em] text-[#A8A398]">
        {icon} {label}
      </div>
      <div className={`font-heading text-3xl mt-1 ${gold ? "text-[#D4AF37]" : accent ? "text-[#EE9CA1]" : "text-[#F5F0E6]"}`}>{value}</div>
      <div className="text-[10px] text-[#575148] mt-1 uppercase tracking-widest">{sub}</div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className={`text-[#A8A398] ${bold ? "font-heading" : ""}`}>{label}</span>
      <span className={`font-mono ${bold ? "text-[#D4AF37] font-heading" : ""}`}>{value}</span>
    </div>
  );
}
